// Lecture recorder store — отражение РЕАЛЬНОГО состояния recorder'а.
//
// Phase 4 рефактор: до этого commit был UI-моком (`start()` лишь ставил
// status='recording', timer крутился независимо от микрофона). Теперь store
// — это thin state-mirror, куда `useRecorder` хук пушит реальный lifecycle
// (создание lecture_session на сервере → MediaRecorder/Capacitor capture →
// uploadChunk pipeline → finalize). Подробности — `hooks/use-recorder.ts`.
//
// Store специально хранит **только** UI-relevant state. mediaStream держим
// здесь же, чтобы waveform (AnalyserNode) мог его подписать без проп-drilling
// через 3 уровня компонентов. setMediaStream — единственный side-effect ref.

import { create } from "zustand";

export type RecorderStatus =
	| "idle"
	| "starting" // create() в полёте — между нажатием кнопки и первым chunk
	| "recording"
	| "paused"
	| "stopping" // finalize() в полёте — между стопом и navigate
	| "uploading"
	| "processing"
	| "error";

/**
 * Захват или отправка записи в полёте: перезагрузка страницы оборвала бы их и
 * выбросила звук, ещё не доехавший до сервера.
 */
export function isRecordingInFlight(status: RecorderStatus): boolean {
	return (
		status === "starting" ||
		status === "recording" ||
		status === "paused" ||
		status === "stopping" ||
		status === "uploading"
	);
}

interface RecorderState {
	status: RecorderStatus;
	elapsedMs: number;
	chunksUploaded: number;
	chunksTotal: number;
	/**
	 * Локально эмитнутых чанков из AudioPort (до upload/confirm).
	 * 0 при живой волне >~20с → пайплайн захвата молчит (см. NoChunksBanner).
	 */
	chunksEmitted: number;
	/**
	 * wall-clock старта текущей capture-сессии (start/continue).
	 * Нужен watchdog'у: elapsedMs при continue включает прошлую сессию.
	 */
	captureStartedAt: number | null;
	/**
	 * Чанков в persistent upload-очереди (idb-keyval) — ожидают доставки
	 * после сбоев сети. >0 → бейдж на live-экране; см. lib/recording-upload-queue.
	 */
	chunksPending: number;
	/** ID активной записи на сервере. null = ещё не создана. */
	recordingId: string | null;
	error: string | null;
	/** MediaStream для AnalyserNode-визуализации (web only; capacitor=null). */
	mediaStream: MediaStream | null;
	/** Label инпут-устройства («AirPods», «MacBook Pro Microphone»). */
	deviceLabel: string | null;

	// ─── actions ───────────────────────────────────────────────────────
	setStatus: (status: RecorderStatus) => void;
	setRecordingId: (id: string | null) => void;
	setMediaStream: (stream: MediaStream | null) => void;
	setDeviceLabel: (label: string | null) => void;
	setError: (error: string | null) => void;
	incrementChunksUploaded: () => void;
	incrementChunksEmitted: () => void;
	setChunksUploaded: (count: number) => void;
	setChunksEmitted: (count: number) => void;
	setCaptureStartedAt: (ts: number | null) => void;
	setChunksTotal: (total: number) => void;
	setChunksPending: (pending: number) => void;
	setElapsedMs: (ms: number) => void;
	tick: (ms: number) => void;
	reset: () => void;
}

const INITIAL: Omit<
	RecorderState,
	| "setStatus"
	| "setRecordingId"
	| "setMediaStream"
	| "setDeviceLabel"
	| "setError"
	| "incrementChunksUploaded"
	| "incrementChunksEmitted"
	| "setChunksUploaded"
	| "setChunksEmitted"
	| "setCaptureStartedAt"
	| "setChunksTotal"
	| "setChunksPending"
	| "setElapsedMs"
	| "tick"
	| "reset"
> = {
	status: "idle",
	elapsedMs: 0,
	chunksUploaded: 0,
	chunksTotal: 0,
	chunksEmitted: 0,
	captureStartedAt: null,
	chunksPending: 0,
	recordingId: null,
	error: null,
	mediaStream: null,
	deviceLabel: null,
};

export const useRecorderStore = create<RecorderState>((set, get) => ({
	...INITIAL,

	setStatus: (status) => set({ status }),
	setRecordingId: (recordingId) => set({ recordingId }),
	setMediaStream: (mediaStream) => set({ mediaStream }),
	setDeviceLabel: (deviceLabel) => set({ deviceLabel }),
	setError: (error) => set((s) => ({ error, status: error ? "error" : s.status })),
	incrementChunksUploaded: () => set((s) => ({ chunksUploaded: s.chunksUploaded + 1 })),
	incrementChunksEmitted: () => set((s) => ({ chunksEmitted: s.chunksEmitted + 1 })),
	setChunksUploaded: (chunksUploaded) => set({ chunksUploaded }),
	setChunksEmitted: (chunksEmitted) => set({ chunksEmitted }),
	setCaptureStartedAt: (captureStartedAt) => set({ captureStartedAt }),
	setChunksTotal: (chunksTotal) => set({ chunksTotal }),
	setChunksPending: (chunksPending) => set({ chunksPending }),
	setElapsedMs: (elapsedMs) => set({ elapsedMs }),
	tick: (ms) => set((s) => (s.status === "recording" ? { elapsedMs: s.elapsedMs + ms } : {})),
	reset: () => {
		// Закрыть mediaStream если он жив — защита от утечек микрофона при
		// неконтролируемом reset (например двойной click на Exit).
		const stream = get().mediaStream;
		stream?.getTracks().forEach((t) => {
			t.stop();
		});
		set(INITIAL);
	},
}));
